import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  CancelStockTransferInput,
  CreateStockTransferInput,
  DispatchStockTransferInput,
  ReceiveStockTransferInput,
  StockTransfer,
  StockTransferListParams,
} from '@/domain/entities/stock-transfer.entity'
import type { StockTransferRepository } from '@/domain/repositories/stock-transfer.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { StockTransferDto } from './stock-transfer.dto'
import { stockTransferMapper } from './stock-transfer.mapper'

export class StockTransferHttpRepository implements StockTransferRepository {
  async list(params: StockTransferListParams): Promise<Paged<StockTransfer>> {
    const raw = await httpClient.get<unknown>('/stock-transfers', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        outlet_id: params.outlet_id,
        from_outlet_id: params.from_outlet_id,
        to_outlet_id: params.to_outlet_id,
        status: params.status,
        from: params.from,
        to: params.to,
        search: params.search,
      },
    })
    const paged = unwrapPaged<StockTransferDto>(raw, params.page ?? 1, params.limit ?? 20)
    return {
      ...paged,
      items: paged.items.map(stockTransferMapper.toStockTransfer),
    }
  }

  async getById(id: string): Promise<StockTransfer> {
    const raw = await httpClient.get<unknown>(`/stock-transfers/${id}`)
    return stockTransferMapper.toStockTransfer(unwrap<StockTransferDto>(raw))
  }

  async create(input: CreateStockTransferInput): Promise<StockTransfer> {
    const raw = await httpClient.post<unknown>('/stock-transfers', input)
    return stockTransferMapper.toStockTransfer(unwrap<StockTransferDto>(raw))
  }

  async dispatch(input: DispatchStockTransferInput): Promise<StockTransfer> {
    const { id, ...body } = input
    const raw = await httpClient.post<unknown>(`/stock-transfers/${id}/dispatch`, body)
    return stockTransferMapper.toStockTransfer(unwrap<StockTransferDto>(raw))
  }

  async receive(input: ReceiveStockTransferInput): Promise<StockTransfer> {
    const { id, ...body } = input
    const raw = await httpClient.post<unknown>(`/stock-transfers/${id}/receive`, body)
    return stockTransferMapper.toStockTransfer(unwrap<StockTransferDto>(raw))
  }

  async cancel(input: CancelStockTransferInput): Promise<StockTransfer> {
    const { id, ...body } = input
    const raw = await httpClient.post<unknown>(`/stock-transfers/${id}/cancel`, body)
    return stockTransferMapper.toStockTransfer(unwrap<StockTransferDto>(raw))
  }
}
